import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useState } from 'react';
import {
  LayoutDashboard, ListOrdered, UserCircle, Stethoscope, Menu as MenuIcon,
  LogOut, Settings, Package, BookOpen, BarChart3, Video, ClipboardList,
  FileText, Search, Calendar, Users, Leaf, X, ChevronRight, AlertTriangle, Cpu, Building2, MessageSquare
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';

const mobileTabsByRole = {
  admin: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Home' },
    { to: '/admin/queue', icon: ListOrdered, label: 'Queue' },
    { to: '/admin/patients', icon: UserCircle, label: 'Patients' },
    { to: '/admin/knowledge-base', icon: BookOpen, label: 'KB' },
  ],
  doctor: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Home' },
    { to: '/admin/queue', icon: ListOrdered, label: 'Queue' },
    { to: '/doctor/patients', icon: UserCircle, label: 'Patients' },
    { to: '/doctor/prescriptions', icon: FileText, label: 'Rx' },
  ],
  counsellor: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Home' },
    { to: '/counsellor/patients', icon: UserCircle, label: 'Patients' },
  ],
};

const overflowByRole = {
  admin: [
    { to: '/admin/pending-reasons', icon: AlertTriangle, label: 'Pending Reasons' },
    { to: '/admin/users', icon: Users, label: 'User Management' },
    { to: '/admin/organisations', icon: Building2, label: 'Organisations' },
    { to: '/admin/medicines', icon: Package, label: 'Medicine Inventory' },
    { to: '/admin/medicine-updates', icon: ClipboardList, label: 'Medicine Updates' },
    { to: '/admin/media-library', icon: Video, label: 'Media Library' },
    { to: '/admin/whatsapp', icon: MessageSquare, label: 'WhatsApp' },
    { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/admin/system-monitor', icon: Cpu, label: 'System Monitor' },
    { to: '/admin/settings', icon: Settings, label: 'Settings' },
  ],
  doctor: [
    { to: '/doctor/consultations', icon: Stethoscope, label: 'Consultations' },
    { to: '/doctor/follow-ups', icon: ClipboardList, label: 'Follow-Ups' },
    { to: '/doctor/medicines', icon: Search, label: 'Medicine Search' },
    { to: '/admin/medicines', icon: Package, label: 'Medicine Inventory' },
    { to: '/admin/knowledge-base', icon: BookOpen, label: 'Knowledge Base' },
  ],
  counsellor: [],
};

const roleLabels = { admin: 'Administrator', doctor: 'Doctor', counsellor: 'Counsellor' };

function MobileBottomNav({ role }) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const location = useLocation();
  const tabs = mobileTabsByRole[role] || mobileTabsByRole.admin;

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-gray-100 flex justify-around items-start pt-2 pb-6 px-2" data-testid="mobile-bottom-nav">
        {tabs.map(tab => {
          const isActive = location.pathname === tab.to || location.pathname.startsWith(tab.to + '/');
          return (
            <NavLink key={tab.to} to={tab.to}
              className="flex flex-col items-center gap-0.5 min-w-[56px] min-h-[44px] active:scale-95 transition-transform"
              data-testid={`mobile-tab-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className={`p-1.5 rounded-2xl transition-colors ${isActive ? 'bg-[#063D2F]/10' : ''}`}>
                <tab.icon className={`w-5 h-5 ${isActive ? 'text-[#063D2F]' : 'text-gray-400'}`} strokeWidth={isActive ? 2.2 : 1.8} />
              </div>
              <span className={`text-[10px] font-medium ${isActive ? 'text-[#063D2F]' : 'text-gray-400'}`}>{tab.label}</span>
            </NavLink>
          );
        })}
        <button onClick={() => setSheetOpen(true)}
          className="flex flex-col items-center gap-0.5 min-w-[56px] min-h-[44px] active:scale-95 transition-transform"
          data-testid="mobile-tab-menu">
          <div className={`p-1.5 rounded-2xl ${sheetOpen ? 'bg-[#063D2F]/10' : ''}`}>
            <MenuIcon className={`w-5 h-5 ${sheetOpen ? 'text-[#063D2F]' : 'text-gray-400'}`} strokeWidth={1.8} />
          </div>
          <span className={`text-[10px] font-medium ${sheetOpen ? 'text-[#063D2F]' : 'text-gray-400'}`}>Menu</span>
        </button>
      </nav>
      <MobileMenuSheet open={sheetOpen} onClose={() => setSheetOpen(false)} role={role} />
    </>
  );
}

function MobileMenuSheet({ open, onClose, role }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const overflow = overflowByRole[role] || [];

  const handleLogout = async () => {
    onClose();
    await logout();
    navigate('/login');
  };

  const handleNav = (to) => {
    onClose();
    navigate(to);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="bottom" className="rounded-t-[28px] max-h-[85vh] pb-8" data-testid="mobile-menu-sheet">
        <SheetHeader className="sr-only"><SheetTitle>Menu</SheetTitle><SheetDescription>Navigation and settings</SheetDescription></SheetHeader>
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-4">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* User card */}
        <div className="flex items-center gap-3 p-4 mx-1 rounded-2xl bg-[#063D2F] mb-4" data-testid="mobile-user-card">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold bg-white/15 text-white">
            {getInitials(user?.name)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-white truncate">{user?.name}</p>
            <p className="text-[10px] font-medium text-white/50">{roleLabels[user?.role] || user?.role}</p>
          </div>
          <Leaf className="w-5 h-5 text-white/30" />
        </div>

        {/* Navigation items */}
        <div className="space-y-0.5 px-1">
          {overflow.map(item => (
            <button key={item.to} onClick={() => handleNav(item.to)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 active:scale-[0.98] transition-all min-h-[44px]"
              data-testid={`mobile-menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <item.icon className="w-5 h-5 text-gray-400" strokeWidth={1.8} />
              <span className="flex-1 text-left">{item.label}</span>
              <ChevronRight className="w-4 h-4 text-gray-300" />
            </button>
          ))}
        </div>

        {/* Sign out */}
        <div className="mt-4 px-1 pt-4 border-t border-gray-100">
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 active:scale-[0.98] transition-all min-h-[44px]"
            data-testid="mobile-logout">
            <LogOut className="w-5 h-5" strokeWidth={1.8} />
            <span>Sign out</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MobileHeader({ title }) {
  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100 flex items-center px-4 h-14" data-testid="mobile-header">
      <div className="flex items-center gap-2 flex-1">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#063D2F]">
          <Leaf className="w-3.5 h-3.5 text-white" strokeWidth={2} />
        </div>
        <h1 className="text-base font-semibold text-gray-900 tracking-tight">{title || 'Eden'}</h1>
      </div>
    </header>
  );
}

export { MobileBottomNav, MobileHeader, MobileMenuSheet };
